import { z } from "zod";
import { PublicIdSchema } from "./common";

export const NotificationType = z.enum([
  "delivery_assigned",
  "delivery_unassigned",
  "delivery_schedule_changed",
  "clock_out_reminder",
  "daily_report_rejected",
  "daily_report_approved",
  "instruction_cancelled",
  "distance_deviation_alert",
  "delay_reported",
  "alcohol_check_failed",
  "vehicle_inspection_ng",
  "license_expiration_alert",
  "news_published",
]);

export const NotificationTarget = z.object({
  kind: z.literal("order"),
  public_id: PublicIdSchema,
  order_number: z.string().nullable(),
});

export const Notification = z.object({
  id: PublicIdSchema,
  type: NotificationType,
  title: z.string(),
  body: z.string(),
  payload: z.unknown().nullable(),
  target: NotificationTarget.nullable(),
  read_at: z.iso.datetime({ offset: true }).nullable(),
  sent_at: z.iso.datetime({ offset: true }).nullable(),
  created_at: z.iso.datetime({ offset: true }),
});

export const ListNotificationsInput = z.object({
  // z.coerce.boolean() は "false" を true 扱いするため preprocess で解釈する。
  unread_only: z
    .preprocess((v) => (typeof v === "string" ? v === "true" : v), z.boolean())
    .optional()
    .default(false),
  /** 指定時はこの種別の通知だけを返す (アラート履歴画面で安全/業務アラートに絞るのに使う)。 */
  types: z.array(NotificationType).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const MarkReadInput = z.object({
  publicId: PublicIdSchema,
});

export type NotificationDto = z.infer<typeof Notification>;
export type NotificationTargetDto = z.infer<typeof NotificationTarget>;
export type NotificationTypeDto = z.infer<typeof NotificationType>;
export type ListNotificationsInputDto = z.infer<typeof ListNotificationsInput>;

/**
 * 通知設定 (F13). ユーザー単位の外部配信チャネル ON/OFF.
 * アプリ内通知は常時保存するため設定対象に含めない.
 * 行が未作成のユーザーはメール / LINE Push 有効 (default true) とみなし、updated_at は null.
 * 配送業務通知は LINE のみ、管理者・事務員向け通知はアプリ内 + LINE で配信する.
 * なお配送指示通知 (delivery_assigned / delivery_unassigned / instruction_cancelled) は
 * 設定を無視して必ず送信する.
 */
export const NotificationSettings = z.object({
  email_enabled: z.boolean(),
  line_push_enabled: z.boolean(),
  updated_at: z.iso.datetime({ offset: true }).nullable(),
});

/** 通知設定の部分更新. 省略したチャネルは現状維持. */
export const UpdateNotificationSettingsInput = z.object({
  email_enabled: z.boolean().optional(),
  line_push_enabled: z.boolean().optional(),
});

export type NotificationSettingsDto = z.infer<typeof NotificationSettings>;
export type UpdateNotificationSettingsInputDto = z.infer<typeof UpdateNotificationSettingsInput>;
